import { Fragment, useMemo, type ReactNode } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
  type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronRight } from 'lucide-react';
import { cx } from '../../lib/util/cx';
import type { TableSort } from '../../lib/table/tableSearch';

export type { TableSort };
import { EmptyState } from '../feedback/EmptyState';
import { ErrorState } from '../feedback/ErrorState';
import { Spinner } from '../feedback/LoadingState';

/**
 * The admin data table (DS `DataTable` + TanStack Table, D3).
 *
 * Visual contract from the DS/handoff: CSS-grid rows with declared column
 * tracks; cream-warm uppercase header; 52px rows (40 compact); brown-tinted
 * dividers; .04 brown hover wash. Cells truncate with an ellipsis by default;
 * `wrap` columns wrap; `action` columns are never clipped (a button inside a
 * clickable row calls `stopPropagation` itself — handoff rule). Status-pill
 * columns use FIXED px tracks, never `fr`. Below `minWidth` the table scrolls
 * horizontally instead of crushing columns.
 *
 * Data contract: SERVER-driven. The table never sorts or pages rows itself —
 * `sort` comes in (from the URL, via useTableUrlState) and `onSortChange`
 * goes out. `sortDisabled` renders sortable headers visibly but inert, with a
 * tooltip, for endpoints that can't sort yet (FE plan B12).
 */
export interface DataTableColumn<T> {
  id: string;
  header: ReactNode;
  /** CSS grid track. `fr` tracks are wrapped in minmax(0, …). Default '1fr'. */
  width?: string;
  align?: 'left' | 'right' | 'center';
  numeric?: boolean;
  wrap?: boolean;
  action?: boolean;
  /** The BE `sortBy` key this column sorts by; omit for unsortable columns. */
  sortKey?: string;
  cell: (row: T, index: number) => ReactNode;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  onRowClick?: (row: T) => void;
  compact?: boolean;
  minWidth?: number;
  emptyLabel?: ReactNode;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  sort?: TableSort | null;
  onSortChange?: (sort: TableSort | null) => void;
  /** Show sortable headers as disabled (BE can't sort this list yet). */
  sortDisabled?: boolean | string;
  /**
   * A full-width line of small text at the bottom of a row — for a list too
   * long for a column (e.g. every locale in a state). Return null for none.
   */
  renderSubline?: (row: T) => ReactNode | null;
  /**
   * Expandable rows: return content for a row, or null for none. A row is
   * either clickable (`onRowClick`, opens something) or expandable, never
   * both — with `onRowClick` set this is ignored and no chevron renders.
   */
  renderExpanded?: (row: T) => ReactNode | null;
  expanded?: Record<string, boolean>;
  onExpandedChange?: (expanded: Record<string, boolean>) => void;
  /** Accessible table name. */
  label?: string;
}

const EXPANDER_TRACK = '26px';

export function gridTemplate(widths: Array<string | undefined>): string {
  return widths.map((w = '1fr') => (/fr$/.test(w) ? `minmax(0, ${w})` : w)).join(' ');
}

// Rows are `gap-4` (16px) between tracks and `px-5` (20px a side).
const TRACK_GAP_PX = 16;
const ROW_PADDING_PX = 40;

/**
 * The narrowest the table can be without its rows overflowing: every fixed px
 * track (and the px floor of a `minmax(Npx, …)`), plus the gaps and padding. A
 * `minWidth` below this would let the rows run past the header's background
 * when the table scrolls sideways, so the table never goes narrower.
 */
export function fixedTracksWidth(widths: Array<string | undefined>): number {
  const tracks = widths.map((w = '1fr') => {
    const px = /^(?:minmax\(\s*)?(\d+(?:\.\d+)?)px/.exec(w.trim());
    return px ? Number(px[1]) : 0;
  });
  return tracks.reduce((a, b) => a + b, 0) + TRACK_GAP_PX * Math.max(0, widths.length - 1) + ROW_PADDING_PX;
}

/** A header click: asc → desc → cleared (the server's or screen's default). */
export function nextSort(current: TableSort | null | undefined, key: string): TableSort | null {
  if (!current || current.key !== key) return { key, dir: 'asc' };
  if (current.dir === 'asc') return { key, dir: 'desc' };
  return null; // third click returns to the server's default order
}

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  onRowClick,
  compact = false,
  minWidth = 760,
  emptyLabel = 'Nothing here yet.',
  loading = false,
  error,
  onRetry,
  sort,
  onSortChange,
  sortDisabled = false,
  renderExpanded,
  renderSubline,
  expanded = {},
  onExpandedChange,
  label,
}: DataTableProps<T>) {
  const expandable = !!renderExpanded && !onRowClick;

  const columnDefs = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map((c) => ({
        id: c.id,
        header: () => c.header,
        cell: (ctx) => c.cell(ctx.row.original, ctx.row.index),
        enableSorting: !!c.sortKey,
      })),
    [columns],
  );

  const sorting: SortingState = useMemo(() => {
    if (!sort) return [];
    const col = columns.find((c) => c.sortKey === sort.key);
    return col ? [{ id: col.id, desc: sort.dir === 'desc' }] : [];
  }, [sort, columns]);

  const table = useReactTable({
    data: rows,
    columns: columnDefs,
    getRowId: (row) => getRowId(row),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => (expandable ? renderExpanded(row.original) !== null : false),
    manualSorting: true,
    manualPagination: true,
    state: { sorting, expanded: expanded as ExpandedState },
    onExpandedChange: (updater) => {
      const next = typeof updater === 'function' ? updater(expanded as ExpandedState) : updater;
      onExpandedChange?.(next === true ? {} : (next as Record<string, boolean>));
    },
  });

  const widths = [...(expandable ? [EXPANDER_TRACK] : []), ...columns.map((c) => c.width)];
  const grid = gridTemplate(widths);
  const tableMinWidth = minWidth ? Math.max(minWidth, fixedTracksWidth(widths)) : undefined;
  const disabledTip = typeof sortDisabled === 'string' ? sortDisabled : 'Sorting not available yet';
  const bodyRows = table.getRowModel().rows;

  return (
    <div
      className={cx(
        'overflow-y-hidden rounded-app-card border border-panel-border bg-white font-body',
        minWidth ? 'overflow-x-auto' : 'overflow-x-hidden',
      )}
    >
      <div role="table" aria-label={label} aria-busy={loading || undefined} style={{ minWidth: tableMinWidth }}>
        <div role="rowgroup">
          <div
            role="row"
            className="grid items-center gap-4 bg-cream-warm px-5 py-2.5 text-[11px] font-bold uppercase tracking-[.14em] text-muted"
            style={{ gridTemplateColumns: grid }}
          >
            {expandable && <div role="columnheader" aria-label="Expand" />}
            {table.getFlatHeaders().map((header) => {
              const col = columns.find((c) => c.id === header.column.id)!;
              const active = sort && col.sortKey === sort.key ? sort.dir : null;
              const ariaSort =
                active === 'asc' ? 'ascending' : active === 'desc' ? 'descending' : col.sortKey ? 'none' : undefined;
              const content = flexRender(header.column.columnDef.header, header.getContext());
              return (
                <div
                  key={header.id}
                  role="columnheader"
                  aria-sort={ariaSort}
                  className={cx('min-w-0 whitespace-nowrap', alignClass(col.align))}
                >
                  {col.sortKey ? (
                    <button
                      type="button"
                      disabled={!!sortDisabled || !onSortChange}
                      title={sortDisabled ? disabledTip : undefined}
                      onClick={() => onSortChange?.(nextSort(sort, col.sortKey!))}
                      className={cx(
                        'inline-flex max-w-full items-center gap-1 border-0 bg-transparent p-0 text-inherit [font:inherit] [letter-spacing:inherit] [text-transform:inherit]',
                        col.align === 'right' && 'flex-row-reverse',
                        'disabled:cursor-not-allowed enabled:hover:text-ink',
                      )}
                    >
                      <span className="truncate">{content}</span>
                      {active === 'asc' ? (
                        <ArrowUp size={12} aria-hidden="true" />
                      ) : active === 'desc' ? (
                        <ArrowDown size={12} aria-hidden="true" />
                      ) : (
                        <ArrowUpDown size={12} aria-hidden="true" className="opacity-40" />
                      )}
                    </button>
                  ) : (
                    content
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div role="rowgroup" className="relative">
          {loading && rows.length > 0 && (
            <div className="absolute inset-0 z-[1] flex items-start justify-center bg-[rgba(255,255,255,.55)] pt-6">
              <Spinner />
            </div>
          )}
          {error && rows.length === 0 ? (
            <ErrorState error={error} onRetry={onRetry} compact />
          ) : loading && rows.length === 0 ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={emptyLabel} compact />
          ) : (
            bodyRows.map((row, i) => {
              const canExpand = row.getCanExpand();
              const isExpanded = row.getIsExpanded();
              const clickable = !!onRowClick || canExpand;
              const activate = () => (onRowClick ? onRowClick(row.original) : canExpand && row.toggleExpanded());
              return (
                <Fragment key={row.id}>
                  <div
                    role="row"
                    tabIndex={clickable ? 0 : undefined}
                    aria-expanded={canExpand ? isExpanded : undefined}
                    onClick={clickable ? activate : undefined}
                    onKeyDown={
                      clickable
                        ? (e) => {
                            if (e.target !== e.currentTarget) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              activate();
                            }
                          }
                        : undefined
                    }
                    className={cx(
                      'grid items-center gap-4 px-5 text-sm text-ink transition-colors duration-fast',
                      compact ? 'min-h-10 py-1.5' : 'min-h-[52px] py-2.5',
                      i > 0 && 'border-t border-panel-border',
                      clickable &&
                        'cursor-pointer hover:bg-[rgba(143,68,32,.04)] focus-visible:bg-[rgba(143,68,32,.04)]',
                    )}
                    style={{ gridTemplateColumns: grid }}
                  >
                    {expandable && (
                      <div role="cell" className="flex text-muted">
                        {canExpand &&
                          (isExpanded ? (
                            <ChevronDown size={16} aria-hidden="true" />
                          ) : (
                            <ChevronRight size={16} aria-hidden="true" />
                          ))}
                      </div>
                    )}
                    {row.getVisibleCells().map((cell) => {
                      const col = columns.find((c) => c.id === cell.column.id)!;
                      return (
                        <div
                          key={cell.id}
                          role="cell"
                          data-dt-cell=""
                          data-dt-wrap={col.wrap ? '' : undefined}
                          data-dt-action={col.action ? '' : undefined}
                          className={cx(
                            alignClass(col.align),
                            col.wrap ? 'whitespace-normal' : 'whitespace-nowrap',
                            col.numeric && 'tabular-nums',
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      );
                    })}
                    {(() => {
                      const sub = renderSubline?.(row.original);
                      return sub ? (
                        <div
                          role="cell"
                          className="-mt-2 whitespace-normal text-xs leading-snug text-muted"
                          style={{ gridColumn: '1 / -1' }}
                        >
                          {sub}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  {isExpanded && renderExpanded && (
                    <div className="border-t border-panel-border bg-cream-warm">{renderExpanded(row.original)}</div>
                  )}
                </Fragment>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function alignClass(align?: 'left' | 'right' | 'center') {
  return align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
}
